import React from "react";
import { Modal, Pressable, StyleSheet, TextInput, View } from "react-native";
import CustomDropdown, { type CustomDropdownOption } from "../../common/general/CustomDropDown/CustomDropdown";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { UploadCloudIcon } from "../../svgIcons";

type CourseUploadDocumentModalProps = {
  visible: boolean;
  onClose: () => void;
  selectedFileName: string | null;
  selectedFileSize: number | null;
  courseName: string;
  courseCode: string;
  term: string;
  termOptions: CustomDropdownOption[];
  uploadInProgress: boolean;
  submitInProgress: boolean;
  ctaDisabled: boolean;
  ctaDisabledReason: string | null;
  allowedMimeTypes: string[];
  maxFileSize: number;
  onSelectFile: () => Promise<void> | void;
  onClearFile: () => void;
  onCourseNameChange: (value: string) => void;
  onCourseCodeChange: (value: string) => void;
  onTermChange: (value: string) => void;
  onSubmit: () => Promise<void> | void;
};

const bytesToMbText = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)}MB`;

function CourseUploadDocumentModal({
  visible,
  onClose,
  selectedFileName,
  selectedFileSize,
  courseName,
  courseCode,
  term,
  termOptions,
  uploadInProgress,
  submitInProgress,
  ctaDisabled,
  ctaDisabledReason,
  allowedMimeTypes,
  maxFileSize,
  onSelectFile,
  onClearFile,
  onCourseNameChange,
  onCourseCodeChange,
  onTermChange,
  onSubmit,
}: CourseUploadDocumentModalProps) {
  const acceptedFileTypesHint = [".docx", ".pdf", ".imscc"];
  const hasServerMimeConstraints = Array.isArray(allowedMimeTypes) && allowedMimeTypes.length > 0;
  const maxFileSizeHint = maxFileSize > 0 ? `max ${bytesToMbText(maxFileSize)}` : "server size limits apply";
  const supportText = hasServerMimeConstraints
    ? `${acceptedFileTypesHint.join(", ")} (${maxFileSizeHint})`
    : ".docx, .pdf, or .imscc Common Cartridge";

  return (
    <Modal
      transparent={true}
      visible={visible}
      animationType="fade"
      onRequestClose={onClose}
      role="dialog"
      accessibilityRole="dialog"
      accessibilityLabel="Upload document modal"
      aria-modal="true"
      accessibilityViewIsModal={true}>
      <View style={styles.overlay}>
        <View style={styles.container}>
          <View style={styles.header}>
            <View>
              <CustomText
                text="Upload Document"
                size="s"
                textType="display"
                weight="bold"
                style={styles.title}
                useTranslationText={false}
              />
              <CustomText
                text="Upload a course document or Common Cartridge file, then complete intake setup before modernization."
                size="xs"
                style={styles.subtitle}
                useTranslationText={false}
              />
            </View>
            <Pressable accessibilityRole="button" accessibilityLabel="Close upload document modal" onPress={onClose}>
              <CustomText text="×" size="l" style={styles.closeText} useTranslationText={false} />
            </Pressable>
          </View>

          <View style={styles.body}>
            <Pressable
              style={styles.uploadZone}
              onPress={() => void onSelectFile()}
              disabled={uploadInProgress || submitInProgress}>
              <View style={styles.uploadIconWrap}>
                <UploadCloudIcon width={24} height={24} color="#475467" />
              </View>
              {uploadInProgress ? (
                <CustomText
                  text="Uploading..."
                  size="s"
                  weight="bold"
                  style={styles.uploadLinkText}
                  useTranslationText={false}
                />
              ) : selectedFileName ? (
                <CustomText
                  text="Replace file"
                  size="s"
                  weight="bold"
                  style={styles.uploadLinkText}
                  useTranslationText={false}
                />
              ) : (
                <View style={styles.uploadInlineRow}>
                  <CustomText
                    text="Click to upload"
                    size="s"
                    weight="bold"
                    style={styles.uploadLinkText}
                    useTranslationText={false}
                  />
                  <CustomText
                    text=" or drag and drop"
                    size="s"
                    style={styles.uploadHintText}
                    useTranslationText={false}
                  />
                </View>
              )}
              {selectedFileName ? (
                <CustomText text={selectedFileName} size="s" style={styles.uploadHintText} useTranslationText={false} />
              ) : null}
              {selectedFileName ? (
                <Pressable onPress={onClearFile} style={styles.clearFileButton}>
                  <CustomText text="Clear file" size="xs" style={styles.clearFileText} useTranslationText={false} />
                </Pressable>
              ) : null}
              <CustomText
                text={selectedFileSize ? `${supportText} • selected ${bytesToMbText(selectedFileSize)}` : supportText}
                size="xs"
                style={styles.uploadFileTypeText}
                useTranslationText={false}
              />
            </Pressable>

            <View>
              <CustomText text="Course Name" size="xs" weight="medium" useTranslationText={false} />
              <TextInput
                placeholder="e.g., Introduction to Criminal Justice"
                placeholderTextColor="#98A2B3"
                style={styles.input}
                value={courseName}
                onChangeText={onCourseNameChange}
              />
            </View>

            <View style={styles.row}>
              <View style={styles.fieldHalf}>
                <CustomText text="Course Code" size="xs" weight="medium" useTranslationText={false} />
                <TextInput
                  placeholder="e.g., CJ101"
                  placeholderTextColor="#98A2B3"
                  style={styles.input}
                  value={courseCode}
                  onChangeText={onCourseCodeChange}
                />
                <CustomText text="optional" size="xs" style={styles.optionalText} useTranslationText={false} />
              </View>
              <View style={styles.fieldHalf}>
                <CustomText text="Term" size="xs" weight="medium" useTranslationText={false} />
                <CustomDropdown
                  options={termOptions}
                  value={term}
                  onChange={onTermChange}
                  placeholder="Select term"
                  fullWidth={true}
                  dropdownStyle={styles.termDropdownField}
                  dropdownContainerStyle={styles.termDropdownContainer}
                />
              </View>
            </View>
            {ctaDisabledReason ? (
              <CustomText text={ctaDisabledReason} size="xs" style={styles.warningText} useTranslationText={false} />
            ) : null}

            <CustomButton
              text={submitInProgress ? "Preparing intake..." : "Continue to Intake"}
              styleType="primary"
              size="m"
              leftIcon={null}
              rightIcon={null}
              style={[styles.submitButton, ctaDisabled ? styles.submitButtonDisabled : null]}
              textStyle={styles.submitButtonText}
              useTranslationText={false}
              disabled={ctaDisabled || submitInProgress || uploadInProgress}
              onPress={() => void onSubmit()}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(5, 6, 15, 0.55)",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  container: {
    width: "100%",
    maxWidth: 500,
    minHeight: 520,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EAECF0",
    overflow: "visible",
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 12,
  },
  subtitle: {
    color: "#667085",
    marginTop: 4,
    maxWidth: 430,
    lineHeight: 18,
  },
  title: {
    fontSize: 18,
    lineHeight: 24,
  },
  closeText: {
    color: "#667085",
    fontSize: 24,
    lineHeight: 24,
  },
  body: {
    padding: 16,
    gap: 14,
    overflow: "visible",
  },
  uploadZone: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#D0D5DD",
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FCFCFD",
  },
  uploadIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 999,
    backgroundColor: "#F2F4F7",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  uploadLinkText: {
    color: "#3E68FE",
  },
  uploadInlineRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  uploadHintText: {
    color: "#344054",
  },
  uploadFileTypeText: {
    color: "#667085",
    marginTop: 2,
  },
  row: {
    flexDirection: "row",
    gap: 12,
    zIndex: 1000,
    elevation: 1000,
  },
  fieldHalf: {
    flex: 1,
    gap: 4,
    zIndex: 1001,
    elevation: 1001,
  },
  input: {
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
    paddingVertical: 10,
    color: "#101828",
    fontSize: 14,
    backgroundColor: "#FFFFFF",
  },
  optionalText: {
    color: "#667085",
  },
  submitButton: {
    marginTop: 2,
    borderRadius: 8,
    zIndex: 1,
    elevation: 1,
  },
  submitButtonDisabled: {
    backgroundColor: "#A4BCFD",
    borderColor: "#A4BCFD",
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontWeight: "600",
  },
  warningText: {
    color: "#B54708",
  },
  termDropdownField: {
    borderColor: "#D0D5DD",
    minHeight: 44,
  },
  termDropdownContainer: {
    borderColor: "#D0D5DD",
  },
  clearFileButton: {
    marginTop: 4,
  },
  clearFileText: {
    color: "#3E68FE",
  },
});

export default CourseUploadDocumentModal;
